"use client";

import { c as _c } from "react/compiler-runtime";
import { ExternalLink, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import React, { useRef, useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { playTick } from "@/utils/audio";
import DecryptText from "@/components/DecryptText";
import { useNeumorphicTheme } from "@/hooks/useNeumorphicTheme";

// Custom inline SVG for Github with animated tail wag on hover
const GithubIcon = t0 => {
  const $ = _c(6);
  const {
    size: t1
  } = t0;
  const size = t1 === undefined ? 20 : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <style dangerouslySetInnerHTML={{
      __html: "\n      @keyframes github-tail-wag {\n        0%, 100% { transform: rotate(0deg); }\n        15% { transform: rotate(-18deg); }\n        30% { transform: rotate(12deg); }\n        45% { transform: rotate(-12deg); }\n        60% { transform: rotate(8deg); }\n        75% { transform: rotate(-4deg); }\n      }\n      .github-tail {\n        transform-origin: 9px 18px;\n        transform-box: view-box;\n        transition: transform 0.2s ease-in-out;\n      }\n      .group:hover .github-tail {\n        animation: github-tail-wag 2s ease-in-out 1;\n      }\n    "
    }} />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  let t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      overflow: "visible"
    };
    t4 = <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />;
    t5 = <path d="M9 18c-4.51 2-5-2-7-2" className="github-tail" />;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t3 = $[1];
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  if ($[4] !== size) {
    t6 = <>{t2}<svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={t3}>{t4}{t5}</svg></>;
    $[4] = size;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  return t6;
};
interface HoveredImageOverlayProps {
  cardIndex: number;
  hoveredIndex: number;
  imageSrc: string;
  isNeumorphic: boolean;
}
const HoveredImageOverlay = t0 => {
  const $ = _c(16);
  const {
    cardIndex,
    hoveredIndex,
    imageSrc,
    isNeumorphic
  } = t0;
  const colTarget = cardIndex % 2;
  const rowTarget = Math.floor(cardIndex / 2);
  const colSource = hoveredIndex % 2;
  const rowSource = Math.floor(hoveredIndex / 2);
  const deltaCol = colSource - colTarget;
  const deltaRow = rowSource - rowTarget;
  const startX = deltaCol === 0 ? "0%" : deltaCol > 0 ? "105%" : "-105%";
  const startY = deltaRow === 0 ? "0%" : deltaRow > 0 ? "105%" : "-105%";
  let t1;
  if ($[0] !== startX || $[1] !== startY) {
    t1 = {
      x: startX,
      y: startY,
      scale: 0.35,
      opacity: 0
    };
    $[0] = startX;
    $[1] = startY;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: "0%",
      y: "0%",
      scale: 1,
      opacity: 1
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== startX || $[5] !== startY) {
    t3 = {
      x: startX,
      y: startY,
      scale: 0.35,
      opacity: 0
    };
    $[4] = startX;
    $[5] = startY;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      duration: 0.28,
      ease: [0.16, 1, 0.3, 1]
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `absolute inset-0 z-30 overflow-hidden ${isNeumorphic ? "bg-white" : "bg-black"}`;
  let t6;
  if ($[8] !== imageSrc || $[9] !== isNeumorphic) {
    t6 = imageSrc ? <Image src={imageSrc} alt="Hovered Project Preview" fill={true} sizes="(max-width: 1024px) 100vw, 50vw" className={`w-full h-full object-cover border-4 ${isNeumorphic ? "border-black" : "border-white"}`} /> : <div className={`w-full h-full flex items-center justify-center border-4 ${isNeumorphic ? "border-black bg-zinc-100" : "border-white bg-zinc-900"}`}><span className={`font-black uppercase tracking-widest text-sm text-center px-4 ${isNeumorphic ? "text-zinc-400" : "text-zinc-600"}`}>Screenshot Missing</span></div>;
    $[8] = imageSrc;
    $[9] = isNeumorphic;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t1 || $[12] !== t3 || $[13] !== t5 || $[14] !== t6) {
    t7 = <motion.div initial={t1} animate={t2} exit={t3} transition={t4} className={t5}>{t6}</motion.div>;
    $[11] = t1;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
interface Project {
  id?: string;
  title: string;
  subtitle?: string;
  description: string;
  tech: string[];
  github: string;
  demo: string;
  images: string[];
}
interface ProjectsDictionary {
  title: string;
  swipeHint: string;
  viewAll: string;
  hide: string;
  repo: string;
  demo: string;
  offline: string;
  viewGithub: string;
  prev: string;
  next: string;
  pageFormat: string;
  newTab: string;
  list: Project[];
}
interface ProjectCardProps {
  project: Project;
  dict: ProjectsDictionary;
  animate?: boolean;
  disableObserver?: boolean;
  isNeumorphic?: boolean;
  asHeading?: boolean;
  hoveredImages?: string[] | null;
  hoveredTitle?: string | null;
  cardIndex?: number;
  hoveredIndex?: number | null;
  onHover?: (images: string[], title: string, index: number) => void;
  onLeave?: () => void;
}
const ProjectCard = t0 => {
  const $ = _c(83);
  const {
    project,
    dict,
    animate: t1,
    disableObserver: t2,
    isNeumorphic: t3,
    asHeading: t4,
    hoveredImages: t5,
    hoveredTitle: t6,
    cardIndex,
    hoveredIndex: t7,
    onHover,
    onLeave
  } = t0;
  const animate = t1 === undefined ? false : t1;
  const disableObserver = t2 === undefined ? false : t2;
  const isNeumorphic = t3 === undefined ? false : t3;
  const asHeading = t4 === undefined ? true : t4;
  const hoveredImages = t5 === undefined ? null : t5;
  const hoveredTitle = t6 === undefined ? null : t6;
  const hoveredIndex = t7 === undefined ? null : t7;
  const [isToggled, setIsToggled] = useState(false);
  const hasGithub = project.github && project.github !== "" && project.github !== "#";
  const hasDemo = project.demo && project.demo !== "" && project.demo !== "#";
  const mainImage = project.images?.[0] || "";
  let otherImage = "";
  if (hoveredImages && hoveredIndex !== null && cardIndex !== undefined) {
    const imageIndex = cardIndex < hoveredIndex ? cardIndex : cardIndex - 1;
    otherImage = hoveredImages[imageIndex] || "";
  }
  const t8 = `relative overflow-hidden group/card flex flex-col justify-between lg:aspect-[16/9] w-full ${!disableObserver ? "project-card" : ""} ${isToggled ? "mobile-force-hover" : ""} ${isNeumorphic ? "brutalist-container bg-white border-black p-3" : "brutalist-container-dark p-3"}`;
  let t9;
  if ($[0] !== cardIndex || $[1] !== onHover || $[2] !== project.images || $[3] !== project.title) {
    t9 = () => {
      if (window.innerWidth >= 1024) {
        onHover?.(project.images, project.title, cardIndex ?? 0);
      }
    };
    $[0] = cardIndex;
    $[1] = onHover;
    $[2] = project.images;
    $[3] = project.title;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== onLeave) {
    t10 = () => {
      if (window.innerWidth >= 1024) {
        onLeave?.();
      }
    };
    $[5] = onLeave;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  let t11;
  if ($[7] !== cardIndex || $[8] !== hoveredIndex || $[9] !== hoveredTitle || $[10] !== isNeumorphic || $[11] !== otherImage || $[12] !== project.title) {
    t11 = hoveredTitle && hoveredTitle !== project.title && hoveredIndex !== null && cardIndex !== undefined && <HoveredImageOverlay cardIndex={cardIndex} hoveredIndex={hoveredIndex} imageSrc={otherImage} isNeumorphic={isNeumorphic} />;
    $[7] = cardIndex;
    $[8] = hoveredIndex;
    $[9] = hoveredTitle;
    $[10] = isNeumorphic;
    $[11] = otherImage;
    $[12] = project.title;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t11) {
    t12 = <AnimatePresence>{t11}</AnimatePresence>;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== disableObserver || $[17] !== isToggled) {
    t13 = () => {
      if (window.innerWidth < 1024 && disableObserver) {
        setIsToggled(!isToggled);
      }
    };
    $[16] = disableObserver;
    $[17] = isToggled;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== asHeading || $[20] !== project.title) {
    t14 = asHeading ? <h3 className="text-lg sm:text-xl xl:text-2xl font-black uppercase mb-0.5 tracking-tight leading-none">{project.title}</h3> : <div className="text-lg sm:text-xl xl:text-2xl font-black uppercase mb-0.5 tracking-tight leading-none">{project.title}</div>;
    $[19] = asHeading;
    $[20] = project.title;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== isNeumorphic || $[23] !== project.subtitle) {
    t15 = project.subtitle && <div className="mb-0.5"><span className={`inline-block text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded border tracking-wide ${isNeumorphic ? "bg-[#d1d9e6]/70 text-zinc-700 border-zinc-400/70" : "bg-zinc-900 text-zinc-300 border-zinc-700"}`}>{project.subtitle}</span></div>;
    $[22] = isNeumorphic;
    $[23] = project.subtitle;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = isNeumorphic ? "text-zinc-800" : "text-zinc-300";
  let t17;
  if ($[25] !== project.description || $[26] !== t16) {
    t17 = <div className="flex-1 min-h-0 overflow-y-auto pr-1 text-xs sm:text-sm md:text-base font-medium leading-snug whitespace-pre-line custom-scrollbar mt-1"><p className={t16}>{project.description}</p></div>;
    $[25] = project.description;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t14 || $[29] !== t15 || $[30] !== t17) {
    t18 = <div className="flex-1 min-h-0 flex flex-col justify-start">{t14}{t15}{t17}</div>;
    $[28] = t14;
    $[29] = t15;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== isNeumorphic || $[33] !== project.tech) {
    let t20;
    if ($[35] !== isNeumorphic) {
      t20 = (tech, i) => <span key={i} className={`px-2 py-0.5 md:px-2.5 text-[10px] md:text-xs font-bold uppercase tracking-wider transition-all duration-300 ${isNeumorphic ? "bg-[#d1d9e6]/70 text-[#4b5563] rounded-md" : "bg-white text-black"}`}>{tech}</span>;
      $[35] = isNeumorphic;
      $[36] = t20;
    } else {
      t20 = $[36];
    }
    t19 = project.tech.map(t20);
    $[32] = isNeumorphic;
    $[33] = project.tech;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[37] !== t19) {
    t20 = <div className="flex flex-wrap gap-1 mt-auto pt-1">{t19}</div>;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t18 || $[40] !== t20) {
    t21 = <div className="flex flex-col flex-1 min-h-0 justify-between">{t18}{t20}</div>;
    $[39] = t18;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  const t22 = `project-image-overlay hidden absolute inset-0 z-10 ${isNeumorphic ? "bg-white" : "bg-black"}`;
  let t23;
  if ($[42] !== isNeumorphic || $[43] !== mainImage || $[44] !== project.title) {
    t23 = mainImage ? <Image src={mainImage} alt={project.title} fill={true} sizes="(max-width: 1024px) 100vw, 50vw" className={`w-full h-full object-cover border-4 ${isNeumorphic ? "border-black" : "border-white"}`} /> : <div className={`w-full h-full border-4 flex items-center justify-center ${isNeumorphic ? "border-black bg-zinc-100" : "border-white bg-zinc-900"}`}><span className={`font-black uppercase tracking-widest text-sm text-center px-4 ${isNeumorphic ? "text-zinc-400" : "text-zinc-600"}`}>Screenshot Missing</span></div>;
    $[42] = isNeumorphic;
    $[43] = mainImage;
    $[44] = project.title;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== t22 || $[47] !== t23) {
    t24 = <div className={t22}>{t23}</div>;
    $[46] = t22;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t13 || $[50] !== t21 || $[51] !== t24) {
    t25 = <div className="relative flex-1 flex flex-col min-h-0 pb-1 max-lg:cursor-pointer justify-between overflow-hidden" onClick={t13}>{t21}{t24}</div>;
    $[49] = t13;
    $[50] = t21;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  const t26 = `flex flex-wrap gap-3 border-t-4 pt-2 flex-none relative z-20 min-h-[44px] ${isNeumorphic ? "border-black" : "border-white"}`;
  let t27;
  if ($[53] !== dict?.newTab || $[54] !== dict?.repo || $[55] !== hasGithub || $[56] !== isNeumorphic || $[57] !== project.github) {
    t27 = hasGithub && <a href={project.github} target="_blank" rel="noopener noreferrer" className={`group flex items-center gap-1.5 text-sm md:text-base font-bold uppercase px-3 py-1.5 transition-all duration-300 ease-in-out shrink-0 ${isNeumorphic ? "bg-[#e0e5ec] text-[#4b5563] rounded-xl border border-transparent shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] hover:bg-[#d1d9e6] hover:text-[#1e293b] hover:shadow-[8px_8px_16px_rgba(163,177,198,0.7),_-8px_-8px_16px_rgba(255,255,255,0.6)] active:shadow-[inset_4px_4px_8px_rgba(163,177,198,0.6),_inset_-4px_-4px_8px_rgba(255,255,255,0.5)]" : "border-2 border-white text-white hover:bg-white hover:text-black"}`}><GithubIcon size={20} /> {dict?.repo || "Repo"}<span className="sr-only">{dict?.newTab || " (opens in a new tab)"}</span></a>;
    $[53] = dict?.newTab;
    $[54] = dict?.repo;
    $[55] = hasGithub;
    $[56] = isNeumorphic;
    $[57] = project.github;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== dict?.demo || $[60] !== dict?.newTab || $[61] !== hasDemo || $[62] !== isNeumorphic || $[63] !== project.demo) {
    t28 = hasDemo && <a href={project.demo} target="_blank" rel="noopener noreferrer" className={`group flex items-center gap-1.5 text-sm md:text-base font-bold uppercase px-3 py-1.5 transition-all duration-300 ease-in-out shrink-0 ${isNeumorphic ? "bg-[#e0e5ec] text-[#4b5563] rounded-xl border border-transparent shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] hover:bg-[#d1d9e6] hover:text-[#1e293b] hover:shadow-[8px_8px_16px_rgba(163,177,198,0.7),_-8px_-8px_16px_rgba(255,255,255,0.6)] active:shadow-[inset_4px_4px_8px_rgba(163,177,198,0.6),_inset_-4px_-4px_8px_rgba(255,255,255,0.5)]" : "border-2 border-white text-white hover:bg-white hover:text-black"}`}><svg className="w-3.5 h-3.5 me-1 shrink-0 overflow-visible" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="7" className={`animate-ping opacity-60 ${isNeumorphic ? "fill-black" : "fill-current"}`} style={{
          transformOrigin: "8px 8px"
        }} /><circle cx="8" cy="8" r="3.5" className={isNeumorphic ? "fill-black" : "fill-current"} /></svg><span className="sr-only">Active live demo indicator: </span>{dict?.demo || "Live Demo"} <ExternalLink size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5" /><span className="sr-only">{dict?.newTab || " (opens in a new tab)"}</span></a>;
    $[59] = dict?.demo;
    $[60] = dict?.newTab;
    $[61] = hasDemo;
    $[62] = isNeumorphic;
    $[63] = project.demo;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== dict?.offline || $[66] !== hasDemo || $[67] !== hasGithub) {
    t29 = !hasGithub && !hasDemo && <span className="text-sm md:text-base font-bold uppercase text-zinc-500 flex items-center cursor-not-allowed">{dict?.offline || "[ Offline / Local Build ]"}</span>;
    $[65] = dict?.offline;
    $[66] = hasDemo;
    $[67] = hasGithub;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t26 || $[70] !== t27 || $[71] !== t28 || $[72] !== t29) {
    t30 = <div className={t26}>{t27}{t28}{t29}</div>;
    $[69] = t26;
    $[70] = t27;
    $[71] = t28;
    $[72] = t29;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== t10 || $[75] !== t12 || $[76] !== t25 || $[77] !== t30 || $[78] !== t8 || $[79] !== t9) {
    t31 = <div className={t8} onMouseEnter={t9} onMouseLeave={t10}>{t12}{t25}{t30}</div>;
    $[74] = t10;
    $[75] = t12;
    $[76] = t25;
    $[77] = t30;
    $[78] = t8;
    $[79] = t9;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  const cardContent = t31;
  if (animate) {
    let t32;
    if ($[81] !== cardContent) {
      t32 = <div className="animate-slide-up h-full w-full">{cardContent}</div>;
      $[81] = cardContent;
      $[82] = t32;
    } else {
      t32 = $[82];
    }
    return t32;
  }
  return cardContent;
};
interface PlaceholderCardProps {
  index: number;
  hoveredTitle: string | null;
  hoveredImages: string[] | null;
  hoveredIndex: number | null;
  isNeumorphic: boolean;
}
const PlaceholderCard = t0 => {
  const $ = _c(14);
  const {
    index,
    hoveredTitle,
    hoveredImages,
    hoveredIndex,
    isNeumorphic
  } = t0;
  let otherImage = "";
  if (hoveredImages && hoveredIndex !== null) {
    const imageIndex = index < hoveredIndex ? index : index - 1;
    otherImage = hoveredImages[imageIndex] || "";
  }
  const t1 = `relative overflow-hidden w-full aspect-[16/9] ${isNeumorphic ? "brutalist-container bg-white border-black" : "brutalist-container-dark"}`;
  const t2 = index + 1;
  let t3;
  if ($[0] !== t2) {
    t3 = <div className="font-mono text-xs font-bold uppercase tracking-widest text-center flex items-center justify-center h-full opacity-40 select-none">[ BLUEPRINT SLOT 0{t2} ]</div>;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== hoveredIndex || $[3] !== hoveredTitle || $[4] !== index || $[5] !== isNeumorphic || $[6] !== otherImage) {
    t4 = hoveredTitle && hoveredIndex !== null && <HoveredImageOverlay cardIndex={index} hoveredIndex={hoveredIndex} imageSrc={otherImage} isNeumorphic={isNeumorphic} />;
    $[2] = hoveredIndex;
    $[3] = hoveredTitle;
    $[4] = index;
    $[5] = isNeumorphic;
    $[6] = otherImage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <AnimatePresence>{t4}</AnimatePresence>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t5) {
    t6 = <div className={t1}>{t3}{t5}</div>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
interface MobileProjectCardProps {
  project: Project;
  dict: ProjectsDictionary;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  isNeumorphic: boolean;
}
const MobileProjectCard = t0 => {
  const $ = _c(76);
  const {
    project,
    dict,
    index,
    isExpanded,
    onToggle,
    isNeumorphic
  } = t0;
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== activeImageIndex || $[1] !== dict?.demo || $[2] !== dict?.newTab || $[3] !== dict?.offline || $[4] !== dict?.repo || $[5] !== index || $[6] !== isExpanded || $[7] !== isNeumorphic || $[8] !== onToggle || $[9] !== project.demo || $[10] !== project.description || $[11] !== project.github || $[12] !== project.images || $[13] !== project.subtitle || $[14] !== project.tech || $[15] !== project.title) {
    const images = project.images && project.images.length > 0 ? project.images.filter(Boolean) : [];
    const totalImages = images.length;
    const currentImage = images[activeImageIndex] || "";
    const thumb = images[0] || "";
    const hasGithub = project.github && project.github !== "" && project.github !== "#";
    const hasDemo = project.demo && project.demo !== "" && project.demo !== "#";
    let t5;
    if ($[21] !== totalImages) {
      t5 = e => {
        e?.stopPropagation();
        if (totalImages <= 1) {
          return;
        }
        playTick();
        setActiveImageIndex(prev => (prev + 1) % totalImages);
      };
      $[21] = totalImages;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    const nextImage = t5;
    let t6;
    if ($[23] !== totalImages) {
      t6 = e_0 => {
        e_0?.stopPropagation();
        if (totalImages <= 1) {
          return;
        }
        playTick();
        setActiveImageIndex(prev_0 => (prev_0 - 1 + totalImages) % totalImages);
      };
      $[23] = totalImages;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    const prevImage = t6;
    t3 = `relative w-full overflow-hidden transition-shadow duration-300 ${isNeumorphic ? `bg-white border-4 border-black ${isExpanded ? "shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)]" : ""}` : `bg-black border-4 border-white ${isExpanded ? "shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)]" : ""}`}`;
    const t7 = `w-full flex items-stretch gap-0 text-left cursor-pointer select-none ${isNeumorphic ? `transition-colors duration-200 ${isExpanded ? "bg-black text-white" : "bg-white text-black active:bg-zinc-100"}` : `transition-colors duration-200 ${isExpanded ? "bg-white text-black" : "bg-black text-white active:bg-zinc-900"}`}`;
    const t8 = thumb ? <Image src={thumb} alt={project.title} fill={true} sizes="84px" className={`object-cover border-e-4 ${isNeumorphic ? isExpanded ? "border-white" : "border-black" : isExpanded ? "border-black" : "border-white"}`} /> : <div className={`w-full h-full flex items-center justify-center border-e-4 ${isNeumorphic ? "border-black bg-zinc-100" : "border-white bg-zinc-900"}`}><span className="text-[8px] font-black uppercase tracking-wider text-center leading-tight px-1 opacity-40">No Img</span></div>;
    const t9 = `absolute top-0.5 start-0.5 text-[9px] font-black tabular-nums px-1 leading-none py-0.5 ${isExpanded ? isNeumorphic ? "bg-white text-black" : "bg-black text-white" : isNeumorphic ? "bg-black text-white" : "bg-white text-black"}`;
    const t10 = String(index + 1);
    let t11;
    if ($[25] !== t10) {
      t11 = t10.padStart(2, "0");
      $[25] = t10;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    let t12;
    if ($[27] !== t11 || $[28] !== t9) {
      t12 = <span className={t9}>{t11}</span>;
      $[27] = t11;
      $[28] = t9;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] !== t12 || $[31] !== t8) {
      t13 = <div className="relative shrink-0 w-[84px] h-[84px]">{t8}{t12}</div>;
      $[30] = t12;
      $[31] = t8;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    let t14;
    if ($[33] !== project.title) {
      t14 = <span className="text-sm sm:text-base font-black uppercase tracking-tight leading-tight truncate">{project.title}</span>;
      $[33] = project.title;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    let t15;
    if ($[35] !== isExpanded || $[36] !== isNeumorphic || $[37] !== project.subtitle) {
      t15 = project.subtitle && <span className={`text-[10px] font-bold mt-0.5 tracking-wide truncate ${isExpanded ? isNeumorphic ? "text-zinc-300" : "text-zinc-500" : isNeumorphic ? "text-zinc-500" : "text-zinc-400"}`}>{project.subtitle}</span>;
      $[35] = isExpanded;
      $[36] = isNeumorphic;
      $[37] = project.subtitle;
      $[38] = t15;
    } else {
      t15 = $[38];
    }
    let t16;
    if ($[39] !== isExpanded || $[40] !== isNeumorphic || $[41] !== project.tech) {
      let t17;
      if ($[43] !== isExpanded || $[44] !== isNeumorphic) {
        t17 = (t, i) => <span key={i} className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider leading-none ${isExpanded ? isNeumorphic ? "bg-white text-black" : "bg-black text-white border border-white/40" : isNeumorphic ? "bg-[#d1d9e6]/70 text-[#4b5563]" : "bg-white text-black"}`}>{t}</span>;
        $[43] = isExpanded;
        $[44] = isNeumorphic;
        $[45] = t17;
      } else {
        t17 = $[45];
      }
      t16 = project.tech.map(t17);
      $[39] = isExpanded;
      $[40] = isNeumorphic;
      $[41] = project.tech;
      $[42] = t16;
    } else {
      t16 = $[42];
    }
    let t17;
    if ($[46] !== t16) {
      t17 = <div className="flex flex-wrap gap-1 mt-1.5 overflow-hidden">{t16}</div>;
      $[46] = t16;
      $[47] = t17;
    } else {
      t17 = $[47];
    }
    let t18;
    if ($[48] !== t14 || $[49] !== t15 || $[50] !== t17) {
      t18 = <div className="flex-1 flex flex-col justify-center px-3 py-2 min-w-0">{t14}{t15}{t17}</div>;
      $[48] = t14;
      $[49] = t15;
      $[50] = t17;
      $[51] = t18;
    } else {
      t18 = $[51];
    }
    const t19 = `flex items-center px-3 shrink-0 border-s-4 ${isExpanded ? isNeumorphic ? "border-white" : "border-black" : isNeumorphic ? "border-black" : "border-white"}`;
    const t20 = isExpanded ? 180 : 0;
    let t21;
    if ($[52] !== t20) {
      t21 = {
        rotate: t20
      };
      $[52] = t20;
      $[53] = t21;
    } else {
      t21 = $[53];
    }
    let t22;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = {
        duration: 0.22,
        ease: "easeInOut"
      };
      $[54] = t22;
    } else {
      t22 = $[54];
    }
    let t23;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter" /></svg>;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    let t24;
    if ($[56] !== t21) {
      t24 = <motion.div animate={t21} transition={t22}>{t23}</motion.div>;
      $[56] = t21;
      $[57] = t24;
    } else {
      t24 = $[57];
    }
    let t25;
    if ($[58] !== t19 || $[59] !== t24) {
      t25 = <div className={t19}>{t24}</div>;
      $[58] = t19;
      $[59] = t24;
      $[60] = t25;
    } else {
      t25 = $[60];
    }
    if ($[61] !== isExpanded || $[62] !== onToggle || $[63] !== t13 || $[64] !== t18 || $[65] !== t25 || $[66] !== t7) {
      t4 = <button type="button" aria-expanded={isExpanded} onClick={onToggle} className={t7}>{t13}{t18}{t25}</button>;
      $[61] = isExpanded;
      $[62] = onToggle;
      $[63] = t13;
      $[64] = t18;
      $[65] = t25;
      $[66] = t7;
      $[67] = t4;
    } else {
      t4 = $[67];
    }
    T0 = AnimatePresence;
    t1 = false;
    t2 = isExpanded && <motion.div key="expanded" initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.28,
      ease: [0.16, 1, 0.3, 1]
    }} className="overflow-hidden"><div className={`border-t-4 ${isNeumorphic ? "border-black" : "border-white"}`}>{totalImages > 0 && <div className="relative w-full aspect-video sm:aspect-[16/9] bg-zinc-950 overflow-hidden select-none touch-pan-y"><motion.div key={activeImageIndex} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.25} onDragEnd={(_e, t26) => {
            const {
              offset,
              velocity
            } = t26;
            if (offset.x < -30 || velocity.x < -200) {
              nextImage();
            } else {
              if (offset.x > 30 || velocity.x > 200) {
                prevImage();
              }
            }
          }} initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.97
          }} transition={{
            duration: 0.2
          }} className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing"><Image src={currentImage} alt={`${project.title} screenshot ${activeImageIndex + 1}`} fill={true} sizes="100vw" className="object-cover object-top pointer-events-none" /></motion.div>{totalImages > 1 && <><button type="button" aria-label="Previous screenshot" onClick={prevImage} className={`absolute left-2.5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center border-2 transition-transform active:scale-90 ${isNeumorphic ? "bg-white text-black border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : "bg-black text-white border-white shadow-[2px_2px_0px_0px_rgba(255,255,255,1)]"}`}><ChevronLeft size={18} strokeWidth={3} /></button><button type="button" aria-label="Next screenshot" onClick={nextImage} className={`absolute right-2.5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center border-2 transition-transform active:scale-90 ${isNeumorphic ? "bg-white text-black border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : "bg-black text-white border-white shadow-[2px_2px_0px_0px_rgba(255,255,255,1)]"}`}><ChevronRight size={18} strokeWidth={3} /></button></>}{totalImages > 1 && <div className={`absolute top-2 right-2 z-20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider border-2 ${isNeumorphic ? "bg-white text-black border-black" : "bg-black text-white border-white"}`}>{activeImageIndex + 1} / {totalImages}</div>}{totalImages > 1 && <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-2.5 py-1 bg-black/70 backdrop-blur-sm border border-white/20 rounded-full">{images.map((_, di) => {
              const isActive = di === activeImageIndex;
              return <button key={di} type="button" aria-label={`Jump to image ${di + 1}`} onClick={e_1 => {
                e_1.stopPropagation();
                playTick();
                setActiveImageIndex(di);
              }} className={`transition-all duration-200 rounded-full ${isActive ? "w-5 h-2 bg-white" : "w-2 h-2 bg-white/40 hover:bg-white/70"}`} />;
            })}</div>}</div>}<div className="p-3.5 flex flex-col gap-3"><div><h4 className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isNeumorphic ? "text-zinc-500" : "text-zinc-400"}`}>Overview</h4><p className={`text-xs sm:text-sm leading-relaxed font-medium whitespace-pre-line ${isNeumorphic ? "text-zinc-800" : "text-zinc-200"}`}>{project.description}</p></div><div className={`pt-2.5 border-t-2 ${isNeumorphic ? "border-zinc-200" : "border-zinc-800"}`}><h4 className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${isNeumorphic ? "text-zinc-500" : "text-zinc-400"}`}>{"Tech Stack & Tools"}</h4><div className="flex flex-wrap gap-1.5">{project.tech.map((t_0, i_0) => <span key={i_0} className={`px-2 py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider border ${isNeumorphic ? "bg-[#e0e5ec] text-[#1e293b] border-zinc-400/80" : "bg-zinc-900 text-white border-zinc-700"}`}>{t_0}</span>)}</div></div><div className={`flex gap-2 pt-3 border-t-4 ${isNeumorphic ? "border-black" : "border-white"}`}>{hasGithub && <a href={project.github} target="_blank" rel="noopener noreferrer" className={`group flex items-center justify-center gap-2 text-xs sm:text-sm font-black uppercase px-3 py-2.5 flex-1 transition-all duration-200 ${isNeumorphic ? "border-2 border-black text-black hover:bg-black hover:text-white active:bg-black active:text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : "border-2 border-white text-white hover:bg-white hover:text-black active:bg-white active:text-black shadow-[2px_2px_0px_0px_rgba(255,255,255,1)]"}`}><GithubIcon size={16} /> {dict?.repo || "Repo"}<span className="sr-only">{dict?.newTab || " (opens in a new tab)"}</span></a>}{hasDemo && <a href={project.demo} target="_blank" rel="noopener noreferrer" className={`group flex items-center justify-center gap-2 text-xs sm:text-sm font-black uppercase px-3 py-2.5 flex-1 transition-all duration-200 ${isNeumorphic ? "bg-black text-white border-2 border-black hover:bg-zinc-800 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : "bg-white text-black border-2 border-white hover:bg-zinc-100 shadow-[2px_2px_0px_0px_rgba(255,255,255,1)]"}`}><svg className="w-3 h-3 shrink-0 overflow-visible" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="7" className="animate-ping opacity-60 fill-current" style={{
                  transformOrigin: "8px 8px"
                }} /><circle cx="8" cy="8" r="3.5" className="fill-current" /></svg>{dict?.demo || "Live Demo"} <ExternalLink size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /><span className="sr-only">{dict?.newTab || " (opens in a new tab)"}</span></a>}{!hasGithub && !hasDemo && <span className="text-xs font-bold uppercase text-zinc-500 py-2.5 px-3 border-2 border-zinc-700 w-full text-center">{dict?.offline || "[ Offline / Local Build ]"}</span>}</div></div></div></motion.div>;
    $[0] = activeImageIndex;
    $[1] = dict?.demo;
    $[2] = dict?.newTab;
    $[3] = dict?.offline;
    $[4] = dict?.repo;
    $[5] = index;
    $[6] = isExpanded;
    $[7] = isNeumorphic;
    $[8] = onToggle;
    $[9] = project.demo;
    $[10] = project.description;
    $[11] = project.github;
    $[12] = project.images;
    $[13] = project.subtitle;
    $[14] = project.tech;
    $[15] = project.title;
    $[16] = T0;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
  } else {
    T0 = $[16];
    t1 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
  }
  let t5;
  if ($[68] !== T0 || $[69] !== t1 || $[70] !== t2) {
    t5 = <T0 initial={t1}>{t2}</T0>;
    $[68] = T0;
    $[69] = t1;
    $[70] = t2;
    $[71] = t5;
  } else {
    t5 = $[71];
  }
  let t6;
  if ($[72] !== t3 || $[73] !== t4 || $[74] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[72] = t3;
    $[73] = t4;
    $[74] = t5;
    $[75] = t6;
  } else {
    t6 = $[75];
  }
  return t6;
};
export default function Projects(t0) {
  const $ = _c(122);
  const {
    dict
  } = t0;
  const [page, setPage] = useState(0);
  const isNeumorphic = useNeumorphicTheme();
  const [expandedMobileIndex, setExpandedMobileIndex] = useState(null);
  const mobileSwipeRef = useRef(null);
  const [hoveredImages, setHoveredImages] = useState(null);
  const [hoveredTitle, setHoveredTitle] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  let t1;
  if ($[0] !== dict?.list) {
    t1 = dict?.list || [];
    $[0] = dict?.list;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const projects = t1;
  const totalPages = Math.ceil(projects.length / 4) || 1;
  let t2;
  if ($[2] !== totalPages) {
    t2 = () => {
      playTick();
      setHoveredImages(null);
      setHoveredTitle(null);
      setHoveredIndex(null);
      setPage(p => (p + 1) % totalPages);
    };
    $[2] = totalPages;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const nextSlide = t2;
  let t3;
  if ($[4] !== totalPages) {
    t3 = () => {
      playTick();
      setHoveredImages(null);
      setHoveredTitle(null);
      setHoveredIndex(null);
      setPage(p_0 => (p_0 - 1 + totalPages) % totalPages);
    };
    $[4] = totalPages;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const prevSlide = t3;
  let t4;
  if ($[6] !== page || $[7] !== projects) {
    t4 = projects.slice(page * 4, (page + 1) * 4);
    $[6] = page;
    $[7] = projects;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const currentProjects = t4;
  const t5 = dict?.title || "Selected Works";
  let t6;
  let titleFirst;
  if ($[9] !== t5) {
    const titleWords = t5.split(" ");
    titleFirst = titleWords[0];
    t6 = titleWords.slice(1).join(" ");
    $[9] = t5;
    $[10] = t6;
    $[11] = titleFirst;
  } else {
    t6 = $[10];
    titleFirst = $[11];
  }
  const titleRest = t6;
  const t7 = `relative w-full min-h-[100svh] flex flex-col justify-between pt-12 lg:pt-16 pb-8 md:pb-12 px-6 md:px-12 lg:px-24 border-b-8 border-black ${isNeumorphic ? "bg-white text-black" : "bg-black text-white"}`;
  const t8 = `absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] pointer-events-none ${isNeumorphic ? "opacity-30" : "opacity-20"}`;
  const t9 = isNeumorphic ? "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.06), transparent 70%)" : "radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.12), transparent 70%)";
  let t10;
  if ($[12] !== t9) {
    t10 = {
      background: t9
    };
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t8) {
    t11 = <div className={t8} style={t10} />;
    $[14] = t10;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = `absolute inset-0 ${isNeumorphic ? "opacity-20" : "opacity-25"}`;
  const t13 = isNeumorphic ? "radial-gradient(rgba(0, 0, 0, 0.4) 1.2px, transparent 1.2px)" : "radial-gradient(rgba(255, 255, 255, 0.35) 1.2px, transparent 1.2px)";
  let t14;
  if ($[17] !== t13) {
    t14 = {
      backgroundImage: t13,
      backgroundSize: "32px 32px"
    };
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t12 || $[20] !== t14) {
    t15 = <div className={t12} style={t14} />;
    $[19] = t12;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = `absolute inset-0 ${isNeumorphic ? "opacity-15" : "opacity-20"}`;
  const t17 = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'><path d='M48 42v12M42 48h12' stroke='${isNeumorphic ? "%23000000" : "%23ffffff"}' stroke-width='1.5' stroke-linecap='square'/></svg>")`;
  let t18;
  if ($[22] !== t17) {
    t18 = {
      backgroundImage: t17,
      backgroundSize: "96px 96px"
    };
    $[22] = t17;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t16 || $[25] !== t18) {
    t19 = <div className={t16} style={t18} />;
    $[24] = t16;
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t11 || $[28] !== t15 || $[29] !== t19) {
    t20 = <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden select-none" aria-hidden="true">{t11}{t15}{t19}</div>;
    $[27] = t11;
    $[28] = t15;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== titleFirst) {
    t21 = <DecryptText text={titleFirst} />;
    $[31] = titleFirst;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <br />;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== titleRest) {
    t23 = titleRest && <DecryptText text={titleRest} />;
    $[34] = titleRest;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== t21 || $[37] !== t23) {
    t24 = <h2 className="text-4xl sm:text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none">{t21}{t22}{t23}</h2>;
    $[36] = t21;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="h-1.5 md:h-2 bg-current w-10 sm:w-20 md:w-28 shrink-0 self-center" />;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t24) {
    t26 = <div className="flex items-center gap-4 sm:gap-6">{t24}{t25}</div>;
    $[40] = t24;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  const t27 = `hidden lg:block text-lg font-bold uppercase mt-4 tracking-widest ${isNeumorphic ? "text-zinc-500" : "text-zinc-400"}`;
  let t28;
  if ($[42] !== dict || $[43] !== page || $[44] !== totalPages) {
    t28 = dict?.pageFormat ? dict.pageFormat.replace("{current}", `0${page + 1}`).replace("{total}", `0${totalPages}`) : `[ PAGE 0${page + 1} / 0${totalPages} ]`;
    $[42] = dict;
    $[43] = page;
    $[44] = totalPages;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== t27 || $[47] !== t28) {
    t29 = <p className={t27}>{t28}</p>;
    $[46] = t27;
    $[47] = t28;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] !== t26 || $[50] !== t29) {
    t30 = <div className="w-full md:w-auto">{t26}{t29}</div>;
    $[49] = t26;
    $[50] = t29;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  const t31 = `hidden md:block text-xl font-bold uppercase border-b-4 pb-1 transition-colors ${isNeumorphic ? "border-black hover:bg-black hover:text-white" : "border-white hover:bg-white hover:text-black"}`;
  const t32 = dict?.viewGithub || "View full GitHub \u2192";
  const t33 = dict?.newTab || " (opens in a new tab)";
  let t34;
  if ($[52] !== t33) {
    t34 = <span className="sr-only">{t33}</span>;
    $[52] = t33;
    $[53] = t34;
  } else {
    t34 = $[53];
  }
  let t35;
  if ($[54] !== t31 || $[55] !== t32 || $[56] !== t34) {
    t35 = <a href="https://github.com/samybit" target="_blank" rel="noopener noreferrer" className={t31}>{t32}{t34}</a>;
    $[54] = t31;
    $[55] = t32;
    $[56] = t34;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  let t36;
  if ($[58] !== t30 || $[59] !== t35) {
    t36 = <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between mb-4 lg:mb-6 gap-2 md:gap-4 flex-none">{t30}{t35}</div>;
    $[58] = t30;
    $[59] = t35;
    $[60] = t36;
  } else {
    t36 = $[60];
  }
  let t37;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = Array.from({
      length: 4
    });
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] !== currentProjects || $[63] !== dict || $[64] !== hoveredImages || $[65] !== hoveredIndex || $[66] !== hoveredTitle || $[67] !== isNeumorphic || $[68] !== page) {
    t38 = <div className="grid grid-cols-2 gap-4 xl:gap-5 w-full">{t37.map((_, index) => {
        if (index < currentProjects.length) {
          const project = currentProjects[index];
          return <ProjectCard key={`desktop-${page}-${index}`} project={project} dict={dict} animate={true} isNeumorphic={isNeumorphic} hoveredImages={hoveredImages} hoveredTitle={hoveredTitle} cardIndex={index} hoveredIndex={hoveredIndex} onHover={(images, title, idx) => {
            setHoveredImages(images);
            setHoveredTitle(title);
            setHoveredIndex(idx);
          }} onLeave={() => {
            setHoveredImages(null);
            setHoveredTitle(null);
            setHoveredIndex(null);
          }} />;
        } else {
          return <PlaceholderCard key={`desktop-placeholder-${page}-${index}`} index={index} hoveredTitle={hoveredTitle} hoveredImages={hoveredImages} hoveredIndex={hoveredIndex} isNeumorphic={isNeumorphic} />;
        }
      })}</div>;
    $[62] = currentProjects;
    $[63] = dict;
    $[64] = hoveredImages;
    $[65] = hoveredIndex;
    $[66] = hoveredTitle;
    $[67] = isNeumorphic;
    $[68] = page;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  const t39 = `flex flex-col border-4 h-full w-full transition-all duration-200 ease-in-out ${isNeumorphic ? "border-black bg-white brutalist-shadow-static" : "border-white bg-black shadow-[8px_8px_0px_rgba(255,255,255,0.3)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(255,255,255,0.3)] active:translate-x-2 active:translate-y-2 active:shadow-none"}`;
  const t40 = `flex-1 flex flex-col items-center justify-center gap-2 border-b-4 transition-colors group ${isNeumorphic ? "border-black hover:bg-black hover:text-white" : "border-white hover:bg-white hover:text-black"}`;
  let t41;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <ArrowUp size={32} className="group-hover:-translate-y-2 transition-transform" />;
    $[70] = t41;
  } else {
    t41 = $[70];
  }
  const t42 = dict?.prev || "Prev";
  let t43;
  if ($[71] !== t42) {
    t43 = <span className="font-black uppercase tracking-widest text-xs rotate-180 [writing-mode:vertical-rl] rtl:-rotate-180">{t42}</span>;
    $[71] = t42;
    $[72] = t43;
  } else {
    t43 = $[72];
  }
  let t44;
  if ($[73] !== prevSlide || $[74] !== t40 || $[75] !== t43) {
    t44 = <button onClick={prevSlide} className={t40}>{t41}{t43}</button>;
    $[73] = prevSlide;
    $[74] = t40;
    $[75] = t43;
    $[76] = t44;
  } else {
    t44 = $[76];
  }
  const t45 = `flex-1 flex flex-col items-center justify-center gap-2 transition-colors group ${isNeumorphic ? "hover:bg-black hover:text-white" : "hover:bg-white hover:text-black"}`;
  const t46 = dict?.next || "Next";
  let t47;
  if ($[77] !== t46) {
    t47 = <span className="font-black uppercase tracking-widest text-xs [writing-mode:vertical-rl]">{t46}</span>;
    $[77] = t46;
    $[78] = t47;
  } else {
    t47 = $[78];
  }
  let t48;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <ArrowDown size={32} className="group-hover:translate-y-2 transition-transform" />;
    $[79] = t48;
  } else {
    t48 = $[79];
  }
  let t49;
  if ($[80] !== nextSlide || $[81] !== t45 || $[82] !== t47) {
    t49 = <button onClick={nextSlide} className={t45}>{t47}{t48}</button>;
    $[80] = nextSlide;
    $[81] = t45;
    $[82] = t47;
    $[83] = t49;
  } else {
    t49 = $[83];
  }
  let t50;
  if ($[84] !== t39 || $[85] !== t44 || $[86] !== t49) {
    t50 = <div className={t39}>{t44}{t49}</div>;
    $[84] = t39;
    $[85] = t44;
    $[86] = t49;
    $[87] = t50;
  } else {
    t50 = $[87];
  }
  let t51;
  if ($[88] !== t38 || $[89] !== t50) {
    t51 = <div className="relative z-10 hidden lg:grid grid-cols-[1fr_5rem] gap-5 xl:gap-6 flex-1 min-h-0 w-full items-center mb-2 projects-grid-magnet">{t38}{t50}</div>;
    $[88] = t38;
    $[89] = t50;
    $[90] = t51;
  } else {
    t51 = $[90];
  }
  const t52 = `text-[10px] font-bold uppercase tracking-[0.2em] mb-1 ${isNeumorphic ? "text-zinc-400" : "text-zinc-500"}`;
  let t53;
  if ($[91] !== projects.length || $[92] !== t52) {
    t53 = <p className={t52}>{projects.length} Projects</p>;
    $[91] = projects.length;
    $[92] = t52;
    $[93] = t53;
  } else {
    t53 = $[93];
  }
  let t54;
  if ($[94] !== dict || $[95] !== expandedMobileIndex || $[96] !== isNeumorphic || $[97] !== projects) {
    let t55;
    if ($[99] !== dict || $[100] !== expandedMobileIndex || $[101] !== isNeumorphic) {
      t55 = (project_0, index_0) => <MobileProjectCard key={`mobile-card-${project_0.id || index_0}`} project={project_0} dict={dict} index={index_0} isExpanded={expandedMobileIndex === index_0} onToggle={() => setExpandedMobileIndex(expandedMobileIndex === index_0 ? null : index_0)} isNeumorphic={isNeumorphic} />;
      $[99] = dict;
      $[100] = expandedMobileIndex;
      $[101] = isNeumorphic;
      $[102] = t55;
    } else {
      t55 = $[102];
    }
    t54 = projects.map(t55);
    $[94] = dict;
    $[95] = expandedMobileIndex;
    $[96] = isNeumorphic;
    $[97] = projects;
    $[98] = t54;
  } else {
    t54 = $[98];
  }
  const t55 = `flex items-center justify-center gap-2 w-full mt-2 p-4 text-base font-black uppercase text-center transition-all duration-200 ${isNeumorphic ? "bg-black text-white border-4 border-black active:bg-zinc-800" : "bg-white text-black border-4 border-white active:bg-zinc-100"}`;
  let t56;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <GithubIcon size={18} />;
    $[103] = t56;
  } else {
    t56 = $[103];
  }
  const t57 = dict?.viewGithub || "View Full GitHub \u2192";
  const t58 = dict?.newTab || " (opens in a new tab)";
  let t59;
  if ($[104] !== t58) {
    t59 = <span className="sr-only">{t58}</span>;
    $[104] = t58;
    $[105] = t59;
  } else {
    t59 = $[105];
  }
  let t60;
  if ($[106] !== t55 || $[107] !== t57 || $[108] !== t59) {
    t60 = <a href="https://github.com/samybit" target="_blank" rel="noopener noreferrer" className={t55}>{t56}{t57}{t59}</a>;
    $[106] = t55;
    $[107] = t57;
    $[108] = t59;
    $[109] = t60;
  } else {
    t60 = $[109];
  }
  let t61;
  if ($[110] !== t53 || $[111] !== t54 || $[112] !== t60) {
    t61 = <div className="relative z-10 flex lg:hidden flex-col gap-2 flex-1 pt-2 pb-4 projects-grid-magnet" ref={mobileSwipeRef}>{t53}{t54}{t60}</div>;
    $[110] = t53;
    $[111] = t54;
    $[112] = t60;
    $[113] = t61;
  } else {
    t61 = $[113];
  }
  let t62;
  if ($[114] !== t36 || $[115] !== t51 || $[116] !== t61) {
    t62 = <div className="w-full max-w-[75rem] mx-auto flex flex-col flex-1 min-h-0">{t36}{t51}{t61}</div>;
    $[114] = t36;
    $[115] = t51;
    $[116] = t61;
    $[117] = t62;
  } else {
    t62 = $[117];
  }
  let t63;
  if ($[118] !== t20 || $[119] !== t62 || $[120] !== t7) {
    t63 = <section id="projects" className={t7}>{t20}{t62}</section>;
    $[118] = t20;
    $[119] = t62;
    $[120] = t7;
    $[121] = t63;
  } else {
    t63 = $[121];
  }
  return t63;
}